import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components/native';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useNavigation } from '@react-navigation/native';

import Form from '../../components/form/Form';
import FormButton from '../../components/form/FormButton';
import FormInput from '../../components/form/FormInput';

import { H2, B, Caption } from '../../components/Typography';
import Spacer from '../../components/Spacer';
import Link from '../../components/Link';

import { StackNavigationProp } from '@react-navigation/stack';
import useSignUp from '../../hooks/user/useSignUp';
import { Platform, useWindowDimensions, View } from 'react-native';
import FormCheckbox from '../../components/form/FormCheckbox';
import FormMaskedInput from '../../components/form/FormMaskedInput';
import WebViewModal from '../modals/WebViewModal';
import accessibilityTags from '../../../accessibilityTags';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';

const passwordMinLength = 8;
const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).*$/;
const passwordRegexErrorMessage =
  'Need at least an uppercase and a lowercase letter, and a number.';

const StyledHorizontalTextView = styled.KeyboardAvoidingView`
  flex: 1;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
`;

const StyledView = styled(KeyboardAvoidingView)`
  flex: 1;
  align-items: center;
`;

const StyledImageBackground = styled.ImageBackground`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const StyledSignUpView = styled.ScrollView<{ isDesktop: boolean }>`
  width: 100%;
  padding: 87px 24px 0px 24px;
`;

const StyledFormView = styled.View`
  width: 100%;
  margin-bottom: 120px;
`;

const StyledRowView = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
`;

const StyledCaption = styled(Caption)`
  margin-right: 8px;
`;

const StyledSignUpContainer = styled.View<{ isDesktop: boolean }>`
  align-items: ${p => (p.isDesktop ? 'center' : 'flex-start')};
  align-self: center;
  max-width: 438px;
  width: 100%;
`;

const SignUpScreen: React.FC = () => {
  const windowDimension = useWindowDimensions();
  const isDesktop = windowDimension.width >= 768;
  const formikRef = useRef<FormikProps<any> | null>();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const { createdUser, creatingUser, createError, createUser } = useSignUp();
  const [openUrl, setOpenUrl] = useState<{ title: string; url: string }>();
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false);
  const [selectedCountryInfo, setSelectedCountryInfo] = useState<string>('');

  // enable/disable form button loading spinner
  useEffect(() => {
    // continue to keep the form disabled after creating the user so that
    // user can't change email field and create another account (to prevent
    // spamming the server)
    formikRef.current?.setSubmitting(Boolean(createdUser) || creatingUser);
  }, [createdUser, creatingUser]);

  // handle create error
  useEffect(() => {
    if (createError) {
      formikRef.current?.setFieldError('email', createError.message);
    }
  }, [createError]);

  // after creating the user login
  useEffect(() => {
    if (createdUser?.email) {
      navigation.navigate('SignUpEmailVerificationScreen', {
        email: createdUser?.email
      });
    }
  }, [createdUser, navigation]);

  return (
    <StyledView behavior='padding'>
      <StyledImageBackground
        resizeMode='stretch'
        source={require('../../assets/intro/background.png')}
      />

      <StyledSignUpView
        isDesktop={isDesktop}
        contentContainerStyle={
          isDesktop ? { flexGrow: 1, justifyContent: 'center' } : {}
        }
      >
        <StyledSignUpContainer isDesktop={isDesktop}>
          <H2
            accessibilityLabel={accessibilityTags.signUpWelcomeID}
            testID={accessibilityTags.signUpWelcomeID}
            color='dark'
          >
            Welcome To Chloe!
          </H2>
          <Spacer size={4} />
          <StyledFormView>
            <Formik
              innerRef={formik => (formikRef.current = formik)}
              initialValues={{
                firstName: '',
                lastName: '',
                email: '',
                password: '',
                confirmPassword: '',
                legal: false,
                phoneNumber: '',
                phoneNumberCode: '',
                notifyForMessages: true,
                notifyForStudy: true
              }}
              validationSchema={Yup.object().shape({
                firstName: Yup.string().required('First name is required.'),
                lastName: Yup.string().required('Last name is required.'),
                email: Yup.string()
                  .email('Invalid email address')
                  .required('Email is required.'),
                password: Yup.string()
                  .matches(passwordRegex, passwordRegexErrorMessage)
                  .min(
                    passwordMinLength,
                    `Minimum ${passwordMinLength} characters.`
                  ),
                confirmPassword: Yup.string()
                  .required('Password confirmation is required.')
                  .oneOf([Yup.ref('password'), null], 'Passwords must match.'),
                legal: Yup.bool().oneOf(
                  [true],
                  'Accept Terms of Service and Privacy Policy is required'
                ),
                phoneNumber: Yup.string(),
                notifyForMessages: Yup.bool(),
                notifyForStudy: Yup.bool()
              })}
              onSubmit={formValues => {
                if (!isPhoneNumberValid) {
                  formValues.notifyForMessages = false;
                  formValues.notifyForStudy = false;
                }
                if (selectedCountryInfo) {
                  const splittedCountryInfo = selectedCountryInfo.split(' ');
                  const dialCode = splittedCountryInfo.slice(-1)[0];
                  formValues.phoneNumber = `${dialCode}${formValues.phoneNumber}`;
                  formValues.phoneNumberCode = splittedCountryInfo[0];
                  setSelectedCountryInfo('');
                }
                createUser(formValues);
              }}
            >
              <Form>
                <FormInput
                  accessibilityLabel={accessibilityTags.signUpFirstNameID}
                  testID={accessibilityTags.signUpFirstNameID}
                  name='firstName'
                  label='First Name'
                  placeholder='First name'
                />
                <FormInput
                  accessibilityLabel={accessibilityTags.signUpLastNameID}
                  testID={accessibilityTags.signUpLastNameID}
                  name='lastName'
                  label='Last Name'
                  placeholder='Last name'
                />
                <FormInput
                  accessibilityLabel={accessibilityTags.signUpEmailID}
                  testID={accessibilityTags.signUpEmailID}
                  autoCapitalize='none'
                  autoCorrect={false}
                  keyboardType='email-address'
                  name='email'
                  label='Email'
                  placeholder='Email address'
                />
                <FormMaskedInput
                  accessibilityLabel={accessibilityTags.signUpPhoneID}
                  testID={accessibilityTags.signUpPhoneID}
                  label='Phone Number'
                  name='phoneNumber'
                  inputType='phone'
                  placeholder='Phone Number (optional)'
                  isValid={(isValid, countryInfo) => {
                    if (isValid) {
                      setIsPhoneNumberValid(true);
                    } else {
                      setIsPhoneNumberValid(false);
                    }
                    if (countryInfo) {
                      setSelectedCountryInfo(countryInfo);
                    }
                  }}
                />
                <StyledRowView>
                  <View>
                    <FormCheckbox
                      disabled={!isPhoneNumberValid}
                      name='notifyForMessages'
                      label={
                        <StyledCaption color='dark'>
                          Text for messages
                        </StyledCaption>
                      }
                    />
                    <Spacer size='small' />
                  </View>
                  <View>
                    <FormCheckbox
                      disabled={!isPhoneNumberValid}
                      name='notifyForStudy'
                      label={
                        <Caption color='dark'>
                          Text for study notifications
                        </Caption>
                      }
                    />
                  </View>
                </StyledRowView>
                <Spacer size={3} />
                <FormInput
                  accessibilityLabel={accessibilityTags.signUpPasswordID}
                  testID={accessibilityTags.signUpPasswordID}
                  secureTextEntry
                  name='password'
                  label='Password'
                  placeholder='Password'
                  textContentType='oneTimeCode'
                />
                <FormInput
                  accessibilityLabel={accessibilityTags.signUpConfirmPasswordID}
                  testID={accessibilityTags.signUpConfirmPasswordID}
                  secureTextEntry
                  name='confirmPassword'
                  returnKeyType='go'
                  submitOnReturn={true}
                  label='Confirm Password'
                  placeholder='Confirm password'
                  textContentType='oneTimeCode'
                />
                <Spacer size={2} />
                <FormCheckbox
                  accessibilityLabel={accessibilityTags.signUpAgreeID}
                  testID={accessibilityTags.signUpAgreeID}
                  name='legal'
                  label={
                    <Caption color='dark'>
                      I have read and agree to the{' '}
                      <Link
                        inverse
                        onPress={() => {
                          const url =
                            'https://peoplescience.health/terms-and-conditions/';
                          if (Platform.OS == 'web') {
                            window.open(url, '_blank');
                          } else {
                            setOpenUrl({ title: 'Terms of Service', url });
                          }
                        }}
                      >
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link
                        inverse
                        onPress={() => {
                          const url =
                            'https://peoplescience.health/privacy-policy/';
                          if (Platform.OS == 'web') {
                            window.open(url, '_blank');
                          } else {
                            setOpenUrl({ title: 'Privacy Policy', url });
                          }
                        }}
                      >
                        Privacy Policy
                      </Link>
                    </Caption>
                  }
                />
                <Spacer size='large' />
                <FormButton
                  disabled={formikRef.current?.isValidating}
                  accessibilityLabel={accessibilityTags.signUpConfirmButtonID}
                  testID={accessibilityTags.signUpConfirmButtonID}
                >
                  Sign Up
                </FormButton>
                <Spacer />
                <B center color='dark'>
                  Already have an account?{' '}
                  <Link
                    accessibilityLabel={accessibilityTags.signUpReturnButtonID}
                    testID={accessibilityTags.signUpReturnButtonID}
                    inverse
                    onPress={() => {
                      navigation.navigate('LoginScreen');
                    }}
                  >
                    Log In
                  </Link>
                </B>
                <Spacer size={4} />
              </Form>
            </Formik>
          </StyledFormView>
        </StyledSignUpContainer>
      </StyledSignUpView>
      {!!openUrl && (
        <WebViewModal
          visible
          title={openUrl.title}
          url={openUrl.url}
          onClose={() => setOpenUrl(undefined)}
        />
      )}
    </StyledView>
  );
};

export default SignUpScreen;
